import StoreAddressAutocompleteController from "./store_address_autocomplete_controller"
import StoreSettingsController from "./store_settings_controller"

import storeMarkerIconUrl from "images/map/restaurant-marker-icon.svg";
import { reducedClutterGoogleMapStyle } from "src/admin/map_styles";

export default class extends StoreAddressAutocompleteController {
  static targets = [
    "map", "addressError", "form",
    "locationType", "locationSource", "autocompleteFields", "locatedInFields", "customFields",
    "autocompleteLabel", "locatedIn", "sublocation",
    "navigationGooglePlaceId", "navigationApplePlaceId",
    "partnerLocationCode", "programmedBeacon", "beaconMajor", "beaconMinor",
    "submitButton",
  ];
  static outlets = ["store-settings"];

  declare mapTarget: HTMLElement;
  declare addressErrorTarget: HTMLDivElement;
  declare formTarget: HTMLFormElement;
  declare locationSourceTarget: HTMLInputElement;
  declare locationTypeTargets: HTMLInputElement[];
  declare autocompleteFieldsTarget: HTMLDivElement;
  declare locatedInFieldsTarget: HTMLDivElement;
  declare customFieldsTargets: HTMLDivElement[];
  declare autocompleteLabelTarget: HTMLLabelElement;
  declare locatedInTarget: HTMLInputElement;
  declare sublocationTarget: HTMLInputElement;
  declare hasPartnerLocationCodeTarget: boolean;
  declare partnerLocationCodeTarget: HTMLInputElement;
  declare programmedBeaconTarget: HTMLInputElement;
  declare beaconMajorTarget: HTMLInputElement;
  declare beaconMinorTarget: HTMLInputElement;
  declare navigationGooglePlaceIdTarget: HTMLInputElement;
  declare navigationApplePlaceIdTarget: HTMLInputElement;
  declare submitButtonTarget: HTMLButtonElement;

  declare storeSettingsOutlet: StoreSettingsController;

  map?: google.maps.Map;
  storeMarker?: google.maps.Marker;

  connect(): void {
    super.connect();

    void this.mapsLoaded?.then(() => {
      this.createMap();
      this.setPosition();
    });

    this.configureFormForLocationType();
  }

  disconnect(): void {
    this.map = undefined;
  }

  private createMap(): void {
    this.map = new google.maps.Map(this.mapTarget, {
      zoom: 16,
      maxZoom: 20,
      tilt: 0,
      styles: reducedClutterGoogleMapStyle,
    });

    const iconSize = 32;
    this.storeMarker = new google.maps.Marker({
      title: "Store",
      icon: {
        url: storeMarkerIconUrl,
        scaledSize: new google.maps.Size(iconSize, iconSize),
        anchor: new google.maps.Point(iconSize/2, iconSize/2),
      },
    });
  }

  setPosition() {
    const storeLocation = {
      lat: Number(this.addressLatitudeTarget.value),
      lng: Number(this.addressLongitudeTarget.value),
    };

    if (storeLocation.lat && storeLocation.lng) {
      this.map?.setCenter(storeLocation);
      this.storeMarker?.setPosition(storeLocation);
      this.storeMarker?.setMap(this.map ?? null);
    } else {
      this.map?.setCenter(new google.maps.LatLng(-26.387, 113.373));
      this.storeMarker?.setMap(null);
    }
  }

  coordinatesChanged() {
    this.setPosition();
    this.locationSourceTarget.value = 'unknown';
  }

  autocompleteInputChanged() {
    // force them to choose from the autocomplete
    this.submitButtonTarget.disabled = this.inputChanged;
  }

  autocompleteRequest(query: string): google.maps.places.AutocompleteRequest {
    switch (this.locationType()) {
      case "located_in":
        return {
          input: query,
          includedPrimaryTypes: [
            // We plan for located_in to work with various types of multi-store premises that don't have
            // great street addresses, but so far all the examples we've got so far fall into these types.
            "shopping_mall", "food_court", "market", "stadium",

            // This ideally wouldn't be here, but in practice the food courts within university
            // campuses are coded to "university", so we'll allow it in case we add any of those.
            "university"
          ],
          includedRegionCodes: ["nz"],
          language: "en-NZ",
          region: "nz",
          sessionToken: this.sessionToken,
        };

      default:
        return super.autocompleteRequest(query);
    }
  }

  applyPlace(place: google.maps.places.Place): void {
    super.applyPlace(place);

    this.locationSourceTarget.value = 'geocoded_address';
    this.setPosition();

    switch (this.locationType()) {
      case "located_in":
        this.selectedValue = place.displayName!;
        this.locatedInTarget.value = place.displayName!;
        this.navigationGooglePlaceIdTarget.value = place.id;
        this.navigationApplePlaceIdTarget.value = '';
        break;

      default:
        this.selectedValue = place.formattedAddress!;
        this.locatedInTarget.value = '';
        this.navigationGooglePlaceIdTarget.value = ''; // We could start setting this for street_address too, but haven't seen any benefit yet
        this.navigationApplePlaceIdTarget.value = '';
    }

    this.submitButtonTarget.disabled = false;
  }

  locationTypeChanged(): void {
    this.inputChanged = true;
    this.configureFormForLocationType();
    this.focusAutocomplete();
  }


  configureFormForLocationType(): void {
    switch (this.locationType()) {
      case "street_address":
        this.autocompleteFieldsTarget.classList.remove("is-hidden");
        this.locatedInFieldsTarget.classList.add("is-hidden");
        this.customFieldsTargets.forEach((target) => target.classList.add("is-hidden"));
        this.submitButtonTarget.disabled = this.inputChanged; // force them to choose from the autocomplete
        this.autocompleteLabelTarget.innerText = "Street address";
        break;

      case "located_in":
        this.autocompleteFieldsTarget.classList.remove("is-hidden");
        this.locatedInFieldsTarget.classList.remove("is-hidden");
        this.customFieldsTargets.forEach((target) => target.classList.add("is-hidden"));
        this.submitButtonTarget.disabled = this.inputChanged;
        this.autocompleteLabelTarget.innerText = "Mall/food court";
        break;

      case "custom":
        this.autocompleteFieldsTarget.classList.add("is-hidden");
        this.locatedInFieldsTarget.classList.add("is-hidden");
        this.customFieldsTargets.forEach((target) => target.classList.remove("is-hidden"));
        this.submitButtonTarget.disabled = false;
        break;
    }
  }

  async submit(event: Event): Promise<void> {
    /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
    event.preventDefault();

    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    this.addressErrorTarget.classList.add("is-hidden");

    const csrfToken = (document.querySelector("meta[name='csrf-token']") as HTMLMetaElement)?.content;

    // the usual FormData thing returns keys like `pickup_point[latitude]`, rather than
    // pickup_point: { latitude: ... }, which won't work. there's only a few elements anyway,
    // and we don't want to set the ones that aren't relevant, so let's just be explicit here.
    const pickup_point_params: any = {
      location_type: this.locationType(),
      location_source: this.locationSourceTarget.value,
      full_address: this.addressResultTarget.value,
      latitude: this.addressLatitudeTarget.value,
      longitude: this.addressLongitudeTarget.value,
      navigation_google_place_id: this.navigationGooglePlaceIdTarget.value,
      navigation_apple_place_id: this.navigationApplePlaceIdTarget.value,
    };

    switch (this.locationType()) {
      case "located_in":
        pickup_point_params['located_in'] = this.locatedInTarget.value;
        pickup_point_params['sublocation'] = this.sublocationTarget.value;
        break;

      case "custom":
        if (this.programmedBeaconTarget.checked) {
          pickup_point_params['beacon_type'] = "programmed_beacon";
          pickup_point_params['beacon_major'] = this.beaconMajorTarget.value;
          pickup_point_params['beacon_minor'] = this.beaconMinorTarget.value;
        } else {
          pickup_point_params['beacon_type'] = "none";
        }
        break;
    }

    if (this.hasPartnerLocationCodeTarget) {
      pickup_point_params['partner_location_code'] = this.partnerLocationCodeTarget.value;
    }

    const response = await fetch(this.formTarget.action, {
      method: "POST",
      credentials: "include",
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        pickup_point: pickup_point_params,
      }),
    })

    if (response.ok) {
      const body = await response.json();
      this.storeSettingsOutlet.setPickupPoint((body.id as number), (body.addressForMaps as string));
    } else {
      const data = await response.json();
      this.addressErrorTarget.innerText = (data.error as string);
      this.addressErrorTarget.classList.remove("is-hidden");
    }
    /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
  }

  private locationType(): string | undefined {
    for (const el of this.locationTypeTargets) {
      if (el.checked) return el.value;
    }
  }
}
